import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { exhaustMap, Observable, of, scan, Subject, takeWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FullAddress } from '../../models/address';
import { User } from '../../models/user';
import { AddressService } from '../../services/address.service';
import { ErrorService } from '../../services/error.service';
import { ModalService } from '../../services/modal.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SubscriptionService } from '../../services/subscription.service';
import { UserService } from '../../services/user.service';
import { DropPinModalComponent } from '../drop-pin/drop-pin.modal';
import { ModalComponent } from '../modal/modal.component';

export interface SimpleAddress {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

@Component({
	selector: 'app-address-entry',
	templateUrl: './address-entry.html',
	styleUrls: ['./address-entry.scss']
})

export class AddressEntryComponent implements OnInit, OnDestroy {
	@Input() public address: any;
	@ViewChild('addressBookSearch') public addressBookSearch: ElementRef;
	public addressBookSearchSubscription: any;
	@Output() public addressChange: EventEmitter<Object> = new EventEmitter<Object>();
	public addressEntryMode = 'GOOGLE_SEARCH';
	public addressModalOpen = false;
	public filteredAddresses: any[] =[];
	public form: UntypedFormGroup;
	@Input() public formSubmitted = false;
	@ViewChild('googleSearch') public googleSearch: ElementRef;
	@ViewChild('barcode') public barcode: ElementRef;
	@ViewChild(MatButtonToggleGroup) public group: MatButtonToggleGroup;
	public isLoading = false;
	@Input() public selectedCustomer: any;
	@Output() public setMapPins: EventEmitter<string> = new EventEmitter<string>();
	@Input() public type: string;
	@Input() public disabled: boolean;
	public user = new User();
    private nextPage$ = new Subject();
    public filteredAddressResults$: Observable<SimpleAddress[]>;
	public exactMatch: boolean = false;
	public barcodeSearchSubscription: any [] = []
	public cancelSearch$ = new Subject<void>();


	constructor(private _addressService: AddressService,
				private _errorService: ErrorService,
				private _dialog: MatDialog,
				private _ref: ChangeDetectorRef,
				private _userService: UserService,
				private _modalService: ModalService,
				private _formBuilder: UntypedFormBuilder,
				private _subscriptionService: SubscriptionService,
		        private _snackbarService: SnackbarService) {
		this.user = this._userService.getUser();
	}

	createAddress(address, allowDuplicate, onSuccess, onFail, bypassErrorMessage?) {
		this._addressService.createAddress(address, allowDuplicate).subscribe(addressId => {
			this._addressService.getAddress(addressId).subscribe(newAddress => {
				onSuccess(newAddress);
			}, (error) => {
				this._errorService.error(error, 'getAddress Failed', 'modal');
				onFail(error);
			});
		}, (error) => {
			if (!bypassErrorMessage) {
				this._errorService.error(error, 'createAddress failed', 'modal');
			}
			onFail(error);
		});
	}

	// Centralizes Drop Pin visibility: Google Places enabled, address book not locked,
	// and the Inet User's Map Based Validation (allowMapValidation) permission granted.
	get canDropPin(): boolean {
		return !!(
			this.user?.globalSettings?.enableGooglePlacesSearch &&
			!this.user?.inetUser?.addressBookLocked &&
			this.user?.allowMapValidation
		);
	}

	dropPin() {
		// Defense in depth: reuse the same canDropPin decision the template gates on, so the
		// modal cannot open even if this handler is reached when Drop Pin should be unavailable.
		if (!this.canDropPin) {
			return;
		}
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = false;
		dialogConfig.panelClass = 'fullscreen-modal-panel';
		dialogConfig.data = {type: this.type};
		const dialogRef = this._dialog.open(DropPinModalComponent, dialogConfig);
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.addressEntryMode = 'GOOGLE_SEARCH';
				this.setGoogleAddress(result.address);
				switch (this.type) {
					case 'origin':
						this.setMapPins.emit('DESTINATION');
						break;
					case 'destination':
						this.setMapPins.emit('ORIGIN');
						break;
				}
			} else {
				this.setMapPins.emit('BOTH');
				this.focusInput();
			}
		});
	}

	focusInput() {
		setTimeout(() => {
			switch (this.addressEntryMode) {
				case 'GOOGLE_SEARCH':
					this.googleSearch?.nativeElement?.focus();
					break;
				case 'ADDRESS_BOOK':
					this.addressBookSearch?.nativeElement?.focus();
					break;
				case 'BARCODE_SEARCH':
					this.barcode?.nativeElement?.focus();
					break;
			}
		}, 300);
	}

	initAddressEntry() {
		if (this.user) {
		if (this.user.inetUser.allowAddressBookAccess) {
				this.addressEntryMode = 'ADDRESS_BOOK';
			} else {
				this.addressEntryMode = 'MANUAL_ENTRY';
			}
			if (this.type === 'origin') {
				this.focusInput();
			}
		}
	}

	ngOnDestroy() {
		this._subscriptionService.unSubscribe([this.addressBookSearchSubscription]);
		this._subscriptionService.unSubscribe([this.barcodeSearchSubscription]);
	}

	ngOnInit() {
		this.form = this._formBuilder.group({
			googleSearch: null,
			addressBookSearch: [{value: null}, [Validators.minLength(2)]],
			barcode: [''],
			exactMatch: ['']
		});
		this.initAddressEntry();
		this.addressBookSearchSubscription = this.form.get('addressBookSearch')
			.valueChanges
			.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				filter(value => typeof value === 'string' && value.trim() !== ''),
				tap(() => {
					this.isLoading = true;
					this.filteredAddresses = [];
				}),
				switchMap(value => this._addressService.searchAddresses(value)
					.pipe(
						finalize(() => this.isLoading = false),
						takeUntil(this.cancelSearch$)
					))
			)
			.subscribe((result) => {
				this.filteredAddresses = result.addresses;
			});

		this.form.get('addressBookSearch')
			.valueChanges
			.pipe(
				filter(value => typeof value === 'string'),
				distinctUntilChanged(),
				filter(value => value.trim() !== ''),
				tap(() => {
					this.cancelSearch$.next();
					this.filteredAddresses = [];
				})
			)
			.subscribe();

		const filter$ = this.form.get('addressBookSearch')
			.valueChanges
			.pipe(
				startWith(''),
				debounceTime(200),
				// If the option value is bound to object, after selecting the option the value will change from string to {}.
				// We want to perform search only when the type is string (no match)
				filter((q) => typeof q === 'string')
			);

		this.filteredAddressResults$ = filter$.pipe(
			switchMap((filter) => {
				let currentPage = 1;
				return this.nextPage$.pipe(
					startWith(currentPage),
					// Until the backend responds, ignore NextPage requests.
					exhaustMap((_) => this.getAddressList(filter, currentPage)),
					tap(() => currentPage++),
					// This is a custom operator because we also need the last emitted value.
					// Stop if there are no more pages, or no results at all for the current search text.
					takeWhile((p) => p.length > 0),
					scan(
						(allAddresses: any, newAddresses: any) =>
							allAddresses.concat(newAddresses),
						[]
					)
				);
			})
		);
		this.barcodeSearchSubscription.push(this.form.get('barcode').valueChanges.pipe(debounceTime(500), distinctUntilChanged())
			.subscribe((barcode) => {
				this.isLoading = true;
				this.processBluetoothScan(barcode);
			}));
		this.updateFormControlsState();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.disabled !== undefined) {
			this.updateFormControlsState();
		}
	}

  getAddressList(startsWith: any, page: number): Observable<SimpleAddress[]> {
    const take = 10;
    const skip = page > 0 ? (page - 1) * take : 0;
    return of(this.filteredAddresses.slice(skip, skip + take));
  }



	onValueChange(value) {
		if (value != null) {
			this.addressEntryMode = value;
		}
		this.group.value = this.addressEntryMode;
		if (value === 'ADDRESS_BOOK' || value === 'GOOGLE_SEARCH' || value === 'BARCODE_SEARCH') {
			this.focusInput();
		}
		this.filteredAddresses = [];

	}

	openAddressModal(address, warning, name) {
		if (!this.addressModalOpen) {
			this.addressModalOpen = true;

			let selectedAddress = null;
			if (address) {
				selectedAddress = address;
			}
			const dialogRef = this._modalService.displayAddressModal(selectedAddress, this.user, this.type, warning, name, null, null, null, null, false);
			dialogRef.afterClosed().subscribe((result: any) => {
				this.addressModalOpen = false;
				if (result) {
					this.addressEntryMode = 'MANUAL_ENTRY';
					this.setAddress({...result.address, isTouched: true});
				} else {
					this.focusInput();
					// Modal was cancelled but if there is a selected address, we need to set it again.
					// Preserving the current isTouched state to prevent map from resetting
					if (selectedAddress) {
						// Don't force isTouched to false, maintain the current state
						this.setAddress({...selectedAddress});
					}
				}
			}, () => {
				this.addressModalOpen = false;
				this.focusInput();
			});
		}
	}

	processBluetoothScan(barcode) {
		const sanitizedBarcode = barcode?.replace(/\$/g, '');

		if (sanitizedBarcode?.length > 0) {
			this.barcodeSearch(sanitizedBarcode);
		} else {
			this.isLoading = false;
		}
	}

	saveAddress(address) {
		address.userId = this.user.username;
		if (address.country === 'US') {
			address.nonUsAddress = 0;
		} else if (address.country === 'CA') {
			address.nonUsAddress = 1;
		} else {
			address.nonUsAddress = 2;
		}

		this.createAddress(address, false, (response) => {
			this.addressEntryMode = 'ADDRESS_BOOK';
			this.setAddress({...response, isTouched: true});
		}, (error) => {
			if (error.status === 409) {
				const dialogRef = this._dialog.open(ModalComponent, {
					data: {
						title: 'Warning',
						message: `A similar address point already exists. Are you sure you want to save a duplicate address point?`,
						showCancel: true,
						cancelText: 'No',
						okText: 'Yes'
					}
				});
				dialogRef.afterClosed().subscribe((result) => {
					if (result) {
						this.createAddress(address, true, (response) => {
							this.addressEntryMode = 'ADDRESS_BOOK';
							this.setAddress({...response, isTouched: true});
						}, (error2) => {
							this._errorService.error(error2, 'saveAddress() createAddress failed', 'modal');
						});
					}
				});
			}
		}, true);
	}

	scanAddressBarcode() {
		this.barcodeSearchSubscription.push(this.form.get('barcode').valueChanges.pipe(debounceTime(500), distinctUntilChanged())
			.subscribe((barcode) => {
				this.processBluetoothScan(barcode)
			}));
	}

	searchAddresses(value): Observable<any[]> {
		return this._addressService.searchAddresses(value).pipe(map((addressObj) => {
			return addressObj.addresses;
		}));
	}


	barcodeSearch(barcode) {
		if (this.form.valid) {
			this._addressService.searchAddresses(barcode, null, null, null, null, ['LOCATION_BARCODE', 'LOOKUP_BARCODE'])
				.subscribe((res) => {
					if (this.exactMatch) {
						let cxtId = `0000${this.user.cxtId}`.slice(-4);
						this.filteredAddresses = res.addresses.filter(address => address.barcode === barcode || `X-${cxtId}-I${address.id}` === barcode);
					} else {
						this.filteredAddresses = res.addresses;
					}
					this.isLoading = false;
				}, (error) => {
					this._errorService.error(error, 'searchAddresses failed', 'modal');
					this.isLoading = false;
				})
		} else {
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}


	setAddress = (address: any) => {
		if (address.country === 'CA') {
			if (address.plus4) {
				address.zip = address.zip + " " + address.plus4;
			}
			address.plus4 = null;
		}
		// XD-52722: Add flag to trigger immediate validation for user-selected addresses
		this.addressChange.emit({...address, triggerImmediateValidation: true});
	};

	setAddressBookAddress = (address: any) => {
    if (this.addressEntryMode == 'ADDRESS_BOOK' && address.validated == 0 && this.user?.inetUser?.addressBookLocked) {
      this._modalService.warningModal(
        {
          title: 'Error',
          message: 'This address cannot be validated and you do not have permissions to edit the address. Please select a different address or contact support for assistance.'
        });
      this.addressBookSearch.nativeElement.value = ''
      return false;
    }
		this.setAddress({...address, isTouched: true});
		this.form.get('addressBookSearch').reset(null);
		this.form.get('barcode').reset(null);
		this.filteredAddresses = [];
	};

	setExactMatch(event) {
		this.exactMatch = event.checked;
	}

	setGoogleAddress(e) {
		if (e.geometry) {
			const lat = e.geometry.location.lat();
			const lng = e.geometry.location.lng();
			// Set up a template for the google address to fill out with all required fields
			const g = {
				subpremise: '',
				street_number: '',
				route: '',
				locality: '',
				administrative_area_level_1: '',
				country: '',
				postal_code: '',
				postal_code_suffix: '',
				postal_code_prefix: '',
				name: ''
			};
			// Create a new address letiable to be updated and set as either origin or destination
			const s = new FullAddress();
			// Loop through the google source address and apply all field values to corresponding values in the template g
			for (let i = 0; i < e.address_components.length; i++) {
				g[e.address_components[i].types[0]] = e.address_components[i].short_name;
			}
			// Take newly mapped values in our google template and apply them to the correctly named fields in our new address
			s.address1 = g.street_number + ' ' + g.route;
			s.address2 = g.subpremise;
			s.city = g.locality;
			s.state = g.administrative_area_level_1;
			s.zip = g.postal_code;
			s.plus4 = g.postal_code_suffix;
			s.name = e.name;
			s.userId = this.user.username;
			if (g.country === 'CA') {
				s.nonUsAddress = 1;
				s.country = g.country;
			} else if (g.country != 'US' && g.country != 'CA') {
				s.nonUsAddress = 2;
				s.country = 'OTHER';
				s.city = `${g.locality}, ${ g.administrative_area_level_1} ${g.postal_code} ${g?.postal_code_suffix}`;
			} else {
				s.nonUsAddress = 0;
				s.country = g.country;
			}
			
			if (e.formatted_phone_number) {
				s.phone = e.formatted_phone_number;
			}
			
			if (lat && lng) {
				s.lat = lat;
				s.lon = lng;
				s.validated = 9;
			}
			if (g.country === 'US' || g.country === 'CA') {
				if ((!s.address1 || s.address1.length < 2) ||
					(!s.city) ||
					(!s.state || s.state.length < 2) ||
					(!s.zip || s.zip.length < 5)) {
					this.openAddressModal(s, true, null);
					return;
				}
			}
			if ((s.lat && s.lon) || (g.country != 'US' && g.country != 'CA')) {
				this.setAddress({...s, isTouched: true});
			}
			// Force the view to look for updates
			this._ref.detectChanges();
		}
		this.form.get('googleSearch').reset(null);
	}

	valueMapper(val) {
		if (val) {
			if (val.name) {
				return val.name;
			} else {
				return val.address;
			}
		}
	}

  displayWith(element: any) {
    return element ? element.name : null;
  }

  onScroll(event: any) {
    //Called multiple times after the scroll has reached the threshold position.
    this.nextPage$.next(event);
  }

  private updateFormControlsState() {
		if (this.form) {
			if (this.disabled) {
				this.form.get('addressBookSearch').disable();
				this.form.get('googleSearch').disable();
				this.form.get('barcode').disable();
				this.form.get('exactMatch').disable();
			} else {
				this.form.get('addressBookSearch').enable();
				this.form.get('googleSearch').enable();
				this.form.get('barcode').enable();
				this.form.get('exactMatch').enable();
			}
		}
	}
}

function value(value: any) {
  throw new Error("Function not implemented.");
}
